import { DefaultScheduler, State } from 'ts-fsrs';
import type { CardKind, Deck, Note, Rating, Review } from '../shared/types.ts';
import { cardId, CARD_KINDS } from '../shared/types.ts';

export type Scheduler = Awaited<ReturnType<typeof createScheduler>>;
export type FsrsCard = ReturnType<Scheduler['newCard']>;
export { State };

export function createScheduler() {
  return DefaultScheduler({
    desiredRetention: 0.9,
    learningSteps: ['1m', '10m'],
    relearningSteps: ['10m'],
    // Without this a brand-new card answered Good jumps straight to days out;
    // the 10 minute step gives a second hearing in the same sitting.
    allowModelOverride: false,
    // Fuzz is seeded by card id and rep count, so replaying a log gives the
    // same schedule on every device.
    enableFuzz: true,
  });
}

export interface CardInfo {
  id: string;
  noteId: string;
  kind: CardKind;
  fsrs: FsrsCard;
  firstReviewAt: number | null;
  lastReviewAt: number | null;
}

const NEVER = Date.UTC(2020, 0, 1);

/** A card's state is a pure function of its review log. */
export function replay(s: Scheduler, id: string, reviews: Review[]): CardInfo {
  const { noteId, kind } = splitId(id);
  const live = reviews.filter((r) => !r.deleted).sort((a, b) => a.reviewedAt - b.reviewedAt || cmp(a.id, b.id));
  // ts-fsrs rejects the epoch as a date; any fixed past date does for an unseen card.
  let card = s.newCard({ now: new Date(live[0]?.reviewedAt ?? NEVER), cardId: id });
  for (const r of live) card = s.review({ card, grade: r.rating, now: new Date(r.reviewedAt) }).card;
  return {
    id,
    noteId,
    kind,
    fsrs: card,
    firstReviewAt: live[0]?.reviewedAt ?? null,
    lastReviewAt: live.at(-1)?.reviewedAt ?? null,
  };
}

function cmp(a: string, b: string) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function splitId(id: string): { noteId: string; kind: CardKind } {
  const i = id.lastIndexOf(':');
  return { noteId: id.slice(0, i), kind: id.slice(i + 1) as CardKind };
}

export function previewIntervals(s: Scheduler, card: FsrsCard, now: Date): Record<Rating, string> {
  const out = {} as Record<Rating, string>;
  for (const p of s.preview({ card, now })) {
    out[p.grade as Rating] = formatInterval(p.card.dueAt.getTime() - now.getTime());
  }
  return out;
}

export function formatInterval(ms: number): string {
  const min = ms / 60_000;
  if (min < 1) return '<1m';
  if (min < 60) return `${Math.round(min)}m`;
  const h = min / 60;
  if (h < 24) return `${Math.round(h)}h`;
  const d = h / 24;
  if (d < 30) return `${Math.round(d)}d`;
  if (d < 365) return `${Math.round(d / 30)}mo`;
  return `${(d / 365).toFixed(1)}y`;
}

// ---- the day's queue ----

export interface QueueSettings {
  maxReviews: number;
  dayStartHour: number;
  /** "Learn more" for today, on top of each deck's own new-card limit. */
  extraNewToday: number;
  /** Study one deck, or null for all of them. */
  deckId: string | null;
}

export interface Queue {
  /** Learning cards due now. */
  learning: CardInfo[];
  /** Learning cards due within the learn-ahead window, used only when nothing else is left. */
  learnAhead: CardInfo[];
  review: CardInfo[];
  fresh: CardInfo[];
  doneToday: number;
  newToday: number;
  /** Earliest learning card still pending today, for the "come back in" message. */
  nextLearningAt: number | null;
}

const LEARN_AHEAD_MS = 20 * 60_000;

export function studyDayStart(now: number, hour: number): number {
  const d = new Date(now);
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() > now) d.setDate(d.getDate() - 1);
  return d.getTime();
}

const KIND_ORDER: Record<CardKind, number> = { recognize: 0, produce: 1 };

export function buildQueue(
  decks: Map<string, Deck>,
  notes: Map<string, Note>,
  cards: Map<string, CardInfo>,
  qs: QueueSettings,
  now: number,
): Queue {
  const dayStart = studyDayStart(now, qs.dayStartHour);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  const inScope = (n: Note | undefined): n is Note => {
    if (!n || n.deleted) return false;
    const d = decks.get(n.deckId);
    return !!d && !d.deleted && d.active !== false && (qs.deckId === null || qs.deckId === d.id);
  };

  // Seeing one side of a note gives away the other, so once either card of a
  // note has been answered today its sibling waits for tomorrow — except
  // learning steps, which must happen when they're due.
  const touchedToday = new Set<string>();
  const newTodayByDeck = new Map<string, number>();
  let newToday = 0;
  let reviewsToday = 0;
  let doneToday = 0;
  for (const c of cards.values()) {
    if (c.lastReviewAt === null || c.lastReviewAt < dayStart) continue;
    touchedToday.add(c.id);
    const n = notes.get(c.noteId);
    if (!inScope(n)) continue;
    doneToday++;
    if (c.firstReviewAt! >= dayStart) {
      newToday++;
      newTodayByDeck.set(n.deckId, (newTodayByDeck.get(n.deckId) ?? 0) + 1);
    } else reviewsToday++;
  }
  const siblingTouched = (c: CardInfo) =>
    touchedToday.has(cardId(c.noteId, c.kind === 'recognize' ? 'produce' : 'recognize'));

  const learning: CardInfo[] = [];
  const learnAhead: CardInfo[] = [];
  const review: CardInfo[] = [];
  const freshByDeck = new Map<string, CardInfo[]>();
  let nextLearningAt: number | null = null;

  for (const note of notes.values()) {
    if (!inScope(note)) continue;
    const deck = decks.get(note.deckId)!;
    let freshTaken = false;
    for (const kind of CARD_KINDS) {
      if (!deck.kinds[kind]) continue;
      const c = cards.get(cardId(note.id, kind));
      if (!c) continue;
      const due = c.fsrs.dueAt.getTime();
      switch (c.fsrs.state) {
        case State.New:
          // Only one new card per note a day: the sibling rule would hold the other back anyway.
          if (!freshTaken && !siblingTouched(c)) {
            freshTaken = true;
            const list = freshByDeck.get(deck.id) ?? [];
            list.push(c);
            freshByDeck.set(deck.id, list);
          }
          break;
        case State.Learning:
        case State.Relearning:
          if (due <= now) learning.push(c);
          else if (due <= now + LEARN_AHEAD_MS) learnAhead.push(c);
          if (due > now && due < dayEnd.getTime()) nextLearningAt = Math.min(nextLearningAt ?? due, due);
          break;
        case State.Review:
          if (due < dayEnd.getTime() && !siblingTouched(c)) review.push(c);
          break;
      }
    }
  }

  const byDue = (a: CardInfo, b: CardInfo) => a.fsrs.dueAt.getTime() - b.fsrs.dueAt.getTime();
  learning.sort(byDue);
  learnAhead.sort(byDue);
  review.sort(byDue);

  // Each deck contributes up to its own daily limit, in its own order; then
  // "learn more" extras come from whatever is left, decks in their order.
  const byPosition = (a: CardInfo, b: CardInfo) => {
    const na = notes.get(a.noteId)!;
    const nb = notes.get(b.noteId)!;
    return na.position - nb.position || na.createdAt - nb.createdAt || KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  };
  const deckOrder = [...freshByDeck.keys()].sort((a, b) => decks.get(a)!.position - decks.get(b)!.position);
  const fresh: CardInfo[] = [];
  const leftover: CardInfo[] = [];
  for (const id of deckOrder) {
    const list = freshByDeck.get(id)!.sort(byPosition);
    const limit = Math.max(0, decks.get(id)!.newPerDay - (newTodayByDeck.get(id) ?? 0));
    fresh.push(...list.slice(0, limit));
    leftover.push(...list.slice(limit));
  }
  let beyondLimits = 0;
  for (const [id, n] of newTodayByDeck) beyondLimits += Math.max(0, n - (decks.get(id)?.newPerDay ?? 0));
  fresh.push(...leftover.slice(0, Math.max(0, qs.extraNewToday - beyondLimits)));

  return {
    learning,
    learnAhead,
    review: review.slice(0, Math.max(0, qs.maxReviews - reviewsToday)),
    fresh,
    doneToday,
    newToday,
    nextLearningAt,
  };
}

/**
 * Learning steps first (they are time-sensitive), then reviews with new cards
 * spread evenly through them, then anything in the learn-ahead window.
 */
export function pickNext(q: Queue, lastCardId: string | null): CardInfo | null {
  if (q.learning.length) return avoidRepeat(q.learning, lastCardId);
  const r = q.review.length;
  const f = q.fresh.length;
  if (r || f) {
    if (!f) return avoidRepeat(q.review, lastCardId);
    if (!r) return avoidRepeat(q.fresh, lastCardId);
    const every = Math.max(1, Math.round((r + f) / f));
    return q.doneToday % every === every - 1 ? q.fresh[0] : q.review[0];
  }
  if (q.learnAhead.length) return avoidRepeat(q.learnAhead, lastCardId);
  return null;
}

function avoidRepeat(list: CardInfo[], last: string | null): CardInfo {
  if (list.length > 1 && list[0].id === last) return list[1];
  return list[0];
}
